import { useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";
import { t } from "ttag";

import { usePatientQuery } from "@/app/hooks";
import { ROUTES } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import { consultationsQueriesStore } from "@/features/consultations/api";
import { ScheduleContextProvider } from "@/features/consultations/contexts";
import { useScheduleAppointment } from "@/features/consultations/hooks";
import { APPOINTMENT_TYPE } from "@/features/consultations/models";
import {
  ScheduleAvailableDaysCard,
  ScheduleAvailableTimeSlotCard,
  ScheduleProviderPreferenceCard,
  ScheduleSubmit,
} from "@/features/consultations/sections";
import { LogoLoader } from "@/shared/components";
import { useSettingsStore } from "@/shared/stores";
import { Card } from "@/shared/ui";
import { ScheduleDietitianHeader } from "../sections/schedule-dietitian";

export const ScheduleDietitianScreen = () => {
  const language = useSettingsStore((state) => state.language);
  const { data: location } = usePatientQuery((patient) => patient.location);
  const canAccessNutritionalCounseling = usePortalUserStore(
    (state) => state.canAccessNutritionalCounseling,
  );

  const { appointmentId } = useParams();

  const isReschedule = Boolean(appointmentId);

  const {
    data: appointmentToReschedule,
    isLoading: isLoadingAppointmentToReschedule,
    isSuccess: isSuccessAppointmentToReschedule,
  } = useQuery(
    consultationsQueriesStore.consultations.appointments._ctx.detail(
      appointmentId,
      { appointmentType: APPOINTMENT_TYPE.DIETITIAN },
    ),
  );

  const {
    data: dietitianAppointments,
    isLoading: isLoadingDietitianAppointments,
  } = useQuery({
    ...consultationsQueriesStore.consultations.appointments._ctx.list._ctx
      .upcoming._ctx.dietitian,
    refetchOnMount: "always",
  });

  const { isMutationSuccessful } = useScheduleAppointment();

  const shouldRedirect =
    (!canAccessNutritionalCounseling && !isReschedule) ||
    (dietitianAppointments?.length && !isMutationSuccessful && !isReschedule);

  const isLoading =
    isLoadingAppointmentToReschedule || isLoadingDietitianAppointments;

  if (shouldRedirect) {
    return <Navigate to={ROUTES.COACHING.OVERVIEW} />;
  }

  return (
    <>
      <ScheduleDietitianHeader isReschedule={isReschedule} />

      <LogoLoader show={isLoading} withOverlay />

      {(isSuccessAppointmentToReschedule || !appointmentId) && (
        <ScheduleContextProvider
          appointmentToReschedule={appointmentToReschedule}
          defaultLanguage={language}
          appointmentType={APPOINTMENT_TYPE.DIETITIAN}
          state={location?.state}
        >
          <div className="grid gap-6 md:grid-cols-2 md:grid-rows-[auto_32.5rem]">
            <ScheduleProviderPreferenceCard
              title={`1. ${t`Provider preference`}`}
              className="col-span-full"
            />

            <ScheduleAvailableDaysCard
              title={`2. ${t`Choose an available day`}`}
            />

            <ScheduleAvailableTimeSlotCard titleStep={3}>
              <Card.Footer>
                <ScheduleSubmit />
              </Card.Footer>
            </ScheduleAvailableTimeSlotCard>
          </div>
        </ScheduleContextProvider>
      )}
    </>
  );
};
